import React, { useState, useEffect } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Target,
  Plus,
  Trash2,
  CheckCircle,
  DollarSign,
  Loader2,
  X,
  Sparkles,
  TrendingUp,
  Calendar,
  PiggyBank,
  Plane,
  Home,
  GraduationCap,
  Car,
  Gift,
  Heart,
  Banknote,
  Minus,
  History,
  AlertTriangle,
  Edit2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const goalIcons = {
  'Vacation': Plane,
  'Emergency Fund': PiggyBank,
  'Home': Home,
  'Education': GraduationCap,
  'Car': Car,
  'Gift': Gift,
  'Health': Heart,
  'Other': Target
};

const goalColors = {
  'Vacation': 'hsl(200 80% 55%)',
  'Emergency Fund': 'hsl(160 60% 50%)',
  'Home': 'hsl(38 90% 55%)',
  'Education': 'hsl(280 60% 55%)',
  'Car': 'hsl(210 50% 55%)',
  'Gift': 'hsl(340 80% 55%)',
  'Health': 'hsl(0 70% 55%)',
  'Other': 'hsl(var(--accent))'
};

export default function Goals() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoal, setNewGoal] = useState({ name: '', target: '', category: 'Other', deadline: '' });
  const [contributeAmount, setContributeAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    setLoading(true);
    try {
      const [goalsRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/personal/goals`, { withCredentials: true }),
        axios.get(`${API_URL}/api/personal/goals-summary`, { withCredentials: true })
      ]);
      setGoals(goalsRes.data.goals || []);
      setSummary(summaryRes.data);
    } catch (error) {
      // Demo data
      setGoals([
        { id: '1', name: 'Vacation Fund', target: 5000, saved: 2750, category: 'Vacation', deadline: '2026-06-01' },
        { id: '2', name: 'Emergency Fund', target: 10000, saved: 6500, category: 'Emergency Fund', deadline: null },
        { id: '3', name: 'New Laptop', target: 2000, saved: 800, category: 'Other', deadline: '2026-03-15' }
      ]);
      setSummary({
        summary: { total_goals: 3, completed_goals: 0, total_target: 17000, total_saved: 10050, overall_progress: 59.1, monthly_savings_rate: 500 },
        upcoming_deadlines: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrEditGoal = async () => {
    if (!isAuthenticated) {
      showAuthPrompt('Save Goal');
      return;
    }
    if (!newGoal.name || !newGoal.target) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (editingGoal) {
        await axios.put(`${API_URL}/api/personal/goals/${editingGoal.id}`, {
          name: newGoal.name,
          target: parseFloat(newGoal.target),
          category: newGoal.category,
          deadline: newGoal.deadline || null
        }, { withCredentials: true });
        toast.success('Goal updated!');
      } else {
        await axios.post(`${API_URL}/api/personal/goals`, {
          name: newGoal.name,
          target: parseFloat(newGoal.target),
          category: newGoal.category,
          deadline: newGoal.deadline || null
        }, { withCredentials: true });
        toast.success('Goal created!');
      }
      setShowAddModal(false);
      setEditingGoal(null);
      setNewGoal({ name: '', target: '', category: 'Other', deadline: '' });
      fetchGoals();
    } catch (error) {
      toast.error('Failed to save goal');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setNewGoal({
      name: goal.name,
      target: goal.target.toString(),
      category: goal.category,
      deadline: goal.deadline || ''
    });
    setShowAddModal(true);
  };

  const handleContribute = async (goalId) => {
    if (!isAuthenticated) {
      showAuthPrompt('Add Contribution');
      return;
    }
    if (!contributeAmount || parseFloat(contributeAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/personal/goals/${goalId}/contribute`, {
        amount: parseFloat(contributeAmount)
      }, { withCredentials: true });
      toast.success('Contribution added!');
      setShowContributeModal(null);
      setContributeAmount('');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to add contribution');
    }
  };

  const handleWithdraw = async (goalId) => {
    if (!isAuthenticated) {
      showAuthPrompt('Withdraw');
      return;
    }
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/personal/goals/${goalId}/withdraw`, {
        amount: parseFloat(withdrawAmount),
        reason: withdrawReason || 'Withdrawal'
      }, { withCredentials: true });
      toast.success('Withdrawal completed!');
      setShowWithdrawModal(null);
      setWithdrawAmount('');
      setWithdrawReason('');
      fetchGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw');
    }
  };

  const handleViewHistory = async (goalId) => {
    try {
      const res = await axios.get(`${API_URL}/api/personal/goals/${goalId}/history`, { withCredentials: true });
      setHistoryData(res.data);
      setShowHistoryModal(goalId);
    } catch (error) {
      toast.error('Failed to load history');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!isAuthenticated) {
      showAuthPrompt('Delete Goal');
      return;
    }
    if (!window.confirm('Delete this goal?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/personal/goals/${goalId}`, { withCredentials: true });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const totalTarget = summary?.summary?.total_target || goals.reduce((sum, g) => sum + (g.target || 0), 0);
  const totalSaved = summary?.summary?.total_saved || goals.reduce((sum, g) => sum + (g.saved || 0), 0);
  const completedGoals = summary?.summary?.completed_goals || goals.filter(g => g.saved >= g.target).length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="goals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
            Savings <span className="text-[hsl(var(--accent))]">Goals</span>
          </h1>
          <p className="text-muted-foreground mt-1">Track progress towards your financial goals</p>
        </div>
        <Button onClick={() => { setEditingGoal(null); setNewGoal({ name: '', target: '', category: 'Other', deadline: '' }); setShowAddModal(true); }} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
          <Plus className="w-4 h-4 mr-2" /> New Goal
        </Button>
      </div>

      {/* Upcoming Deadlines Alert */}
      {summary?.upcoming_deadlines?.length > 0 && (
        <Card className="border-amber-500/20 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-400">Upcoming Deadlines</h3>
                <div className="mt-2 space-y-1">
                  {summary.upcoming_deadlines.slice(0, 3).map((item, idx) => (
                    <p key={idx} className="text-sm text-muted-foreground">
                      <span className="font-medium">{item.name}</span> - {item.days_left} days left, need {formatCurrency(item.monthly_needed)}/mo
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Goals</p>
                <p className="text-2xl font-bold">{formatCurrency(totalTarget)}</p>
              </div>
              <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                <Target className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Saved</p>
                <p className="text-2xl font-bold text-emerald-500">{formatCurrency(totalSaved)}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <PiggyBank className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{completedGoals} / {goals.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <CheckCircle className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Rate</p>
                <p className="text-2xl font-bold text-blue-400">{formatCurrency(summary?.summary?.monthly_savings_rate || 0)}</p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Goals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.length > 0 ? (
          goals.map((goal) => {
            const percentage = goal.target > 0 ? Math.min((goal.saved / goal.target) * 100, 100) : 0;
            const isComplete = goal.saved >= goal.target;
            const Icon = goalIcons[goal.category] || Target;
            const color = goalColors[goal.category] || 'hsl(var(--accent))';
            const remaining = Math.max(goal.target - goal.saved, 0);
            
            let daysLeft = null;
            if (goal.deadline) {
              const deadlineDate = new Date(goal.deadline);
              const today = new Date();
              daysLeft = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
            }
            
            return (
              <Card key={goal.id} className={`card-premium ${isComplete ? 'border-emerald-500/50' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}20` }}>
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{goal.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{goal.category}</Badge>
                          {daysLeft !== null && (
                            <span className={daysLeft < 30 ? 'text-amber-500' : ''}>
                              {daysLeft > 0 ? `${daysLeft} days left` : 'Past deadline'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {isComplete && (
                      <Badge className="bg-emerald-500 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" /> Complete!
                      </Badge>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-2xl font-bold" style={{ color }}>{formatCurrency(goal.saved)}</p>
                        <p className="text-sm text-muted-foreground">of {formatCurrency(goal.target)}</p>
                      </div>
                      <p className="text-3xl font-bold text-muted-foreground">{Math.round(percentage)}%</p>
                    </div>
                    
                    <div className="h-3 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${percentage}%`, backgroundColor: isComplete ? 'hsl(160 60% 50%)' : color }}
                      />
                    </div>
                    
                    {!isComplete && (
                      <p className="text-sm text-muted-foreground text-center">
                        {formatCurrency(remaining)} more to reach your goal
                      </p>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    {!isComplete && (
                      <Button 
                        onClick={() => { setShowContributeModal(goal.id); setContributeAmount(''); }} 
                        className="flex-1"
                        style={{ backgroundColor: color }}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    )}
                    {goal.saved > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => { setShowWithdrawModal(goal); setWithdrawAmount(''); setWithdrawReason(''); }}
                        className="flex-1"
                      >
                        <Minus className="w-4 h-4 mr-1" /> Withdraw
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewHistory(goal.id)}
                      title="View History"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditGoal(goal)}
                      title="Edit Goal"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteGoal(goal.id)}
                      className="text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="card-premium md:col-span-2">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No goals yet</p>
              <p className="text-sm mt-1">Create your first savings goal to start tracking</p>
              <Button onClick={() => setShowAddModal(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Create Goal
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Goal Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editingGoal ? 'Edit Goal' : 'Create Savings Goal'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowAddModal(false); setEditingGoal(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Goal Name *</Label>
                <Input
                  placeholder="e.g., Vacation to Hawaii"
                  value={newGoal.name}
                  onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Target Amount *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="5000"
                    value={newGoal.target}
                    onChange={(e) => setNewGoal({ ...newGoal, target: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={newGoal.category}
                  onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
                >
                  {Object.keys(goalIcons).map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Target Date (optional)</Label>
                <Input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button onClick={handleCreateOrEditGoal} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
                {editingGoal ? 'Update Goal' : 'Create Goal'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contribute Modal */}
      {showContributeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Contribution</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowContributeModal(null); setContributeAmount(''); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Amount</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="100"
                    value={contributeAmount}
                    onChange={(e) => setContributeAmount(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Button 
                onClick={() => handleContribute(showContributeModal)} 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                Add to Savings
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Withdraw from {showWithdrawModal.name}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowWithdrawModal(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>
                Available: {formatCurrency(showWithdrawModal.saved)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Amount</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="100"
                    max={showWithdrawModal.saved}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Input
                  placeholder="Emergency expense"
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  className="mt-1"
                />
              </div>
              <Button 
                onClick={() => handleWithdraw(showWithdrawModal.id)} 
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                Withdraw
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && historyData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{historyData.goal_name} History</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setShowHistoryModal(null); setHistoryData(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>
                {formatCurrency(historyData.current_saved)} of {formatCurrency(historyData.target)}
              </CardDescription>
            </CardHeader>
            <CardContent className="max-h-[60vh] overflow-y-auto">
              {historyData.history?.length > 0 ? (
                <div className="space-y-3">
                  {historyData.history.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                      <div>
                        <p className={`font-medium ${item.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {item.amount >= 0 ? '+' : ''}{formatCurrency(item.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item.date).toLocaleDateString()}
                          {item.reason && ` - ${item.reason}`}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Balance: {formatCurrency(item.balance_after)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No transactions yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
